(ns common.schema.entities.ai-summary-configuration.schema
  "Schema for a mutable, tenant-bound AI summary library configuration.

   Common tenant, owner, audit, and soft-delete attributes are inherited from
   the common entity schema. Configuration access never grants access to source
   records. Generic mutation hooks and generation endpoints must recheck
   source-entity and field permissions and capture validated effective settings
   before enqueueing a generation.

   Several invariants span multiple fields and cannot be expressed by the field
   schema alone. Entity-specific generic mutation hooks must enforce those
   invariants atomically as described beside the affected fields below."
  (:require
   [common.schema.entities.ai-summary-configuration.options :as options]
   [common.schema.schema-utils :as su]))

(def schema
  (vec
   (concat
    ;; Purpose: Names the configuration in library lists and deal selectors.
    ;; Justification: A stable human label is easier to identify than prompt text.
    ;; Special instructions: Trim and require a non-blank value; maximum 160
    ;; characters. This is also the entity display-name source.
    (su/text-simple-field
     :ai-summary-configuration/configuration-name
     "Configuration name"
     {:authorization su/access-user-user
      :constraints {:constraint/string-max-length 160}
      :default-field-visibility :in-use-disabled
      :system-required? true
      :system-semantics su/user-provided-system-required-field-semantics})
    ;; Purpose: Explains the intended audience and outcome to library users.
    ;; Justification: Similar configurations may differ in scope or tone while
    ;; sharing nearly identical names.
    ;; Special instructions: Optional, maximum 1,000 characters, and library
    ;; metadata only; do not send it to the AI provider as instructions.
    (su/text-multiline-field
     :ai-summary-configuration/configuration-description
     "Description"
     {:authorization su/access-user-user
      :constraints {:constraint/string-max-length 1000}
      :default-field-visibility :in-use})
    ;; Purpose: Selects private, restricted, or tenant-wide use visibility.
    ;; Justification: Sharing policy must be explicit and independent from the
    ;; list of selected users and teams.
    ;; Special instructions: Default new configurations to private. Restricted
    ;; access consults use-access; other modes ignore it. Sharing never expands
    ;; permissions to deals, attachments, activities, profiles, or persons.
    (su/keyword-field
     :ai-summary-configuration/configuration-access-level
     "Access level"
     options/access-level-options
     {:authorization su/access-user-user
      :default-field-visibility :in-use-disabled
      :system-required? true
      :system-semantics su/user-provided-system-required-field-semantics})
    ;; Purpose: Identifies accounts and teams allowed to use or copy a restricted
    ;; configuration.
    ;; Justification: One mixed reference set supports direct and team sharing
    ;; without duplicating access fields.
    ;; Special instructions: Meaningful only for restricted access. Every
    ;; reference must belong to the configuration tenant. Use access does not
    ;; grant update, delete, ownership-transfer, or sharing-management rights.
    (su/multi-ref-field
     :ai-summary-configuration/use-access
     "Users and teams with use access"
     [:entity.type/account :entity.type/team]
     {:authorization su/access-user-user
      :default-field-visibility :in-use})
    ;; Purpose: Limits where the configuration appears by deal pipeline.
    ;; Justification: Summary formats can be relevant only to particular deal
    ;; workflows while still being reusable across deals in those pipelines.
    ;; Special instructions: An empty set means all deal pipelines. Validate
    ;; that every pipeline is active, deal-capable, and belongs to the tenant.
    (su/multi-ref-field
     :ai-summary-configuration/applicable-pipelines
     "Applicable deal pipelines"
     [:entity.type/pipeline]
     {:authorization su/access-user-user
      :default-field-visibility :in-use})
    ;; Purpose: Selects this configuration automatically for specified pipelines.
    ;; Justification: Applicability alone does not identify the preferred choice
    ;; when several configurations can be used in the same pipeline.
    ;; Special instructions: Enforce at most one default configuration per
    ;; pipeline across the tenant. A default pipeline must also be applicable;
    ;; when applicable-pipelines is empty, any validated tenant deal pipeline is
    ;; eligible. Update competing defaults in the same transaction.
    (su/multi-ref-field
     :ai-summary-configuration/default-pipelines
     "Default deal pipelines"
     [:entity.type/pipeline]
     {:authorization su/access-user-user
      :default-field-visibility :in-use})
    ;; Purpose: Supplies the end-user-editable task instructions for the summary.
    ;; Justification: Different summaries need different emphasis and content
    ;; without changing Zapflow code or the provider output contract.
    ;; Special instructions: Trim and require a non-blank value; maximum 20,000
    ;; characters. Insert it only inside the protected task-instruction envelope.
    ;; It must not replace safety rules, permission checks, or the response schema.
    (su/text-multiline-field
     :ai-summary-configuration/summary-instructions
     "AI summary instructions"
     {:authorization su/access-user-user
      :constraints {:constraint/string-max-length 20000}
      :default-field-visibility :in-use
      :system-required? true
      :system-semantics su/user-provided-system-required-field-semantics})
    ;; Purpose: Declares exactly which record-context branches may be gathered.
    ;; Justification: One keyword set is extensible and avoids twelve persisted
    ;; booleans while still rendering naturally as independent checkboxes.
    ;; Special instructions: Reject unsupported keywords. Each source is
    ;; independent; selecting attachments or persons never implicitly enables
    ;; fields. Apply selection before loading, extracting, serializing, or
    ;; logging data. An empty set means no record context is sent. Bootstrap the
    ;; initial configuration with all options/default-data-sources enabled.
    (su/keyword-multi-field
     :ai-summary-configuration/selected-data-sources
     "Selected data sources"
     options/data-source-options
     {:authorization su/access-user-user
      :default-field-visibility :in-use-disabled})
    ;; Purpose: Discriminates between no style input, a document, and a URL.
    ;; Justification: A canonical mode prevents contradictory style-source state.
    ;; Special instructions: Default to none. Enforce the corresponding document
    ;; or URL invariant on save and clear stale source fields when the mode changes.
    (su/keyword-field
     :ai-summary-configuration/style-guideline-source
     "Style guideline source"
     options/style-guideline-source-options
     {:authorization su/access-user-user
      :default-field-visibility :in-use-disabled
      :system-required? true
      :system-semantics su/user-provided-system-required-field-semantics})
    ;; Purpose: References an uploaded style or brand-guideline document.
    ;; Justification: Some organizations keep approved guidance in files rather
    ;; than on a publicly reachable website.
    ;; Special instructions: Allowed only when style-guideline-source is document.
    ;; Require a readable, virus-cleared, same-tenant attachment the owner may
    ;; access. Extract and sanitize bounded guidance at save time; do not parse the
    ;; file during every generation. Clear this reference for other source modes.
    (su/single-ref-field
     :ai-summary-configuration/style-guideline-document
     "Style guideline document"
     [:entity.type/attachment]
     {:authorization su/access-user-user
      :default-field-visibility :in-use})
    ;; Purpose: Records the web location from which style guidance was imported.
    ;; Justification: It preserves source provenance and supports organizations
    ;; that publish brand guidance instead of uploading a document.
    ;; Special instructions: Allowed only when style-guideline-source is URL.
    ;; Require an absolute public HTTPS URL and apply the shared SSRF protections.
    ;; Fetch, bound, and sanitize content at save time only; generation uses the
    ;; imported snapshot and must not crawl this URL. Clear it for other modes.
    (su/url-field
     :ai-summary-configuration/style-reference-url
     "Style guideline URL"
     {:authorization su/access-user-user
      :default-field-visibility :in-use})
    ;; Purpose: Stores the bounded style guidance produced from the selected file
    ;; or URL for use during generation.
    ;; Justification: A save-time snapshot avoids network/file I/O and source drift
    ;; while an asynchronous generation is executing.
    ;; Special instructions: System-controlled and never accepted directly from a
    ;; client; maximum 20,000 characters. Replace it atomically after successful
    ;; import, clear it for the none mode, and treat its content as untrusted when
    ;; composing prompts. Do not log the content.
    (su/text-multiline-field
     :ai-summary-configuration/imported-style-guidelines
     "Imported style guidelines"
     {:authorization su/access-system-user
      :constraints {:constraint/string-max-length 20000}
      :default-field-visibility :in-use-disabled
      :system-semantics su/system-controlled-optional-field-semantics})
    ;; Purpose: References the optional document template used to render output.
    ;; Justification: Organizations can apply approved layouts without making a
    ;; template mandatory for AI summary generation.
    ;; Special instructions: Validate a same-tenant attachment the owner may use,
    ;; its supported template type, and required placeholders before activation.
    ;; The template is authoritative when present; otherwise render the safe,
    ;; self-contained one-page PDF fallback. Style-source selection is
    ;; independent from template presence.
    (su/single-ref-field
     :ai-summary-configuration/document-template
     "Document template"
     [:entity.type/attachment]
     {:authorization su/access-user-user
      :default-field-visibility :in-use}))))
